import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const TOP_N = 10;
const PIE_MAX_SLICES = 6;

const FIELD_BY_TITLE = {
  OCOM: "region",
  Location: "office",
  Region: "division",
  Subport: "section",
  Division: "unit",
  Section: "cluster",
};

const PIE_COLORS = [
  "#2563eb",
  "#0d9488",
  "#d97706",
  "#7c3aed",
  "#059669",
  "#dc2626",
];

function buildHeadcountRows(title, units = [], employees = []) {
  const field = FIELD_BY_TITLE[title];
  if (!field) return [];

  return units
    .map((item) => ({
      name: item?.name || "Unnamed",
      value: employees.filter((emp) => emp[field] === item.id).length,
    }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
}

function toTopNWithOthers(rows, limit = TOP_N) {
  if (rows.length <= limit) return rows;

  const top = rows.slice(0, limit);
  const othersTotal = rows
    .slice(limit)
    .reduce((sum, row) => sum + row.value, 0);

  if (othersTotal > 0) {
    top.push({ name: "Others", value: othersTotal });
  }
  return top;
}

function truncateLabel(name = "", max = 22) {
  if (name.length <= max) return name;
  return `${name.slice(0, max - 1)}…`;
}

/**
 * Few categories → pie with distinct colors.
 * Many categories → horizontal bar, Top 10 + Others.
 */
const SimplePieChart = ({ title, value = [], employee = [] }) => {
  const chartData = useMemo(() => {
    const rows = buildHeadcountRows(title, value, employee);
    return toTopNWithOthers(rows, TOP_N);
  }, [title, value, employee]);

  const total = chartData.reduce((sum, row) => sum + row.value, 0);
  const usePie = chartData.length > 0 && chartData.length <= PIE_MAX_SLICES;

  if (!chartData.length) {
    return (
      <div style={{ width: "100%", minHeight: 120, padding: "0.75rem" }}>
        <h5 className="mb-1">{title}</h5>
        <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
          No employees assigned
        </p>
      </div>
    );
  }

  if (usePie) {
    return (
      <div
        style={{
          width: "100%",
          padding: "0.5rem",
          overflow: "hidden",
        }}
      >
        <h5 className="mb-1">{title}</h5>
        <p className="text-muted mb-2" style={{ fontSize: "0.8rem" }}>
          Total employees: {total}
        </p>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={85}
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`${entry.name}-${index}`}
                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(count, name) => [`${count} employees`, name]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Legend outside the chart so it stays in document flow and cannot
            spill over the next section title (e.g. Region). */}
        <div className="d-flex flex-wrap gap-2 mt-2 pb-1">
          {chartData.map((entry, index) => (
            <div
              key={`${entry.name}-${index}`}
              className="d-flex align-items-center"
              style={{ fontSize: "0.75rem", maxWidth: "100%" }}
              title={`${entry.name}: ${entry.value}`}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: PIE_COLORS[index % PIE_COLORS.length],
                  flexShrink: 0,
                  marginRight: 6,
                }}
              />
              <span className="text-truncate">
                {truncateLabel(entry.name, 28)} ({entry.value})
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const namedTopCount = chartData.filter((row) => row.name !== "Others").length;
  const hasOthers = chartData.some((row) => row.name === "Others");
  const chartHeight = Math.max(320, chartData.length * 38 + 80);

  return (
    <div style={{ width: "100%", height: chartHeight, padding: "0.5rem" }}>
      <h5 className="mb-1">{title}</h5>
      <p className="text-muted mb-2" style={{ fontSize: "0.8rem" }}>
        Top {namedTopCount}
        {hasOthers ? " + Others" : ""} · Total employees: {total}
      </p>
      <ResponsiveContainer>
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 8, right: 28, left: 4, bottom: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="name"
            width={150}
            tick={{ fontSize: 11 }}
            tickFormatter={truncateLabel}
          />
          <Tooltip
            formatter={(count) => [`${count} employees`, "Headcount"]}
            labelFormatter={(label) => label}
          />
          <Bar
            dataKey="value"
            name="Employees"
            barSize={18}
            radius={[0, 4, 4, 0]}
          >
            {chartData.map((entry) => (
              <Cell
                key={entry.name}
                fill={entry.name === "Others" ? "#94a3b8" : "#2563eb"}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SimplePieChart;
